import React, { useEffect, useCallback, useRef, useState } from "react";
import { connect } from "react-redux";
import { useParams, useNavigate } from 'react-router-dom';
import useRazorpay from "react-razorpay";
import axios from 'axios';
import * as config from "../../assets/js/constant.js";

import {
    Box,
    Stepper,
    Step,
    StepLabel,
    StepContent,
    Button,
    Container,
    Typography,
    Grid,
    Divider,
    CircularProgress,
} from '@mui/material';

import '../MyPujaBookingComponent/MyPujaBookingComponent.css';
import HeaderComponent from '../../common/HeaderComponent/HeaderComponent.js';
import PujaHomaBookingComponent from '../PujaHomaBookingComponent/PujaHomaBookingComponent.js';
import DevoteeDetailsComponent from '../DevoteeDetailsComponent/DevoteeDetailsComponent.js';
import PujaConfirmationComponent from '../PujaConfirmationComponent/PujaConfirmationComponent.js';
import BottomAppBarComponent from '../../common/BottomAppBarComponent/BottomAppBarComponent.js';
import FooterComponent from '../../common/FooterComponent/FooterComponent.js';
import { useImmer } from 'use-immer';
import {
    setpujabookingdetails,
    setpaymentdetails,
    getpujadetail,
    paymentprocess,
    bookPujaComplete
} from '../../store/actions/puja.js';

const MyPujaBookingComponent = ({
    paymentprocess,
    getpujadetail,
    pujadetails,
    setpujabookingdetails,
    setpaymentdetails,
    pujabookingdetails,
    bookPujaComplete
}) => {

    const navigate = useNavigate();
    const { pujaID } = useParams();
    const [Razorpay] = useRazorpay();

    // ---- Puja detail load guard --------------------------------------
    // Waits for the correct puja's details to arrive before rendering the
    // form (avoids flashing an empty/undefined pujadetails object) and
    // bails the user out to the gallery if nothing ever arrives.
    const [isApiLoading, setIsApiLoading] = useState(true);

    useEffect(() => {
        if (pujaID) {
            setIsApiLoading(true);
            getpujadetail(pujaID);
        }
    }, [pujaID, getpujadetail]);

    useEffect(() => {
        if (!isApiLoading) return;
        if (pujadetails && Number(pujadetails.PujaID) === Number(pujaID)) {
            setIsApiLoading(false);
        } else if (pujadetails && Object.keys(pujadetails).length === 0) {
            const checkDataTimeout = setTimeout(() => {
                if (Object.keys(pujadetails).length === 0) {
                    navigate("/puja-gallery");
                }
            }, 5000);
            return () => clearTimeout(checkDataTimeout);
        }
    }, [pujadetails, pujaID, navigate, isApiLoading]);

    // ---- Form state -----------------------------------------------------
    const PUJAHOMAINITIALVALUE = {
        entity: "PUJA",
        pujaID: "",
        pujaName: "",
        pujaDate: "",
        currency: 'INR',
        Amount: 0,
        pujaPerson: "",
        pujaDetail: ""
    };

    const PUJAHOMAUSERVALUE = {
        pujaUserName: "",
        pujaUserContact: "",
        pujaUserAlt: "",
        pujamailName: "",
        pujamailAddress: "",
        pujamailPincode: "",
        pujamailMobile: "",
        userEmail: "",
        isEmailValid: false,
        userPanNo: "",
        isPanValid: true, // PAN is optional; empty counts as valid
        userComments: ""
    };

    const PUJAPAYMENTVALUES = {
        MappingID: "",
        OrderID: "",
        userPanNo: "",
        userEmail: "",
        userComments: ""
    };

    const [getpujahomadetails, setpujahomadetails] = useImmer(PUJAHOMAINITIALVALUE);
    const [getpujahomauserdetails, setpujahomauserdetails] = useImmer(PUJAHOMAUSERVALUE);
    const [, setgetpaymentdetails] = useImmer(PUJAPAYMENTVALUES);
    const [getloader, setloader] = useImmer(false);
    const [activeStep, setActiveStep] = useImmer(0);

    useEffect(() => {
        if (pujadetails?.PujaID && String(pujadetails.PujaID) === String(pujaID)) {
            setpujahomadetails((draft) => {
                draft.pujaID = pujadetails.PujaID;
                draft.pujaName = pujadetails.PujaName;
                draft.Amount = pujadetails.Amount;
            });
        }
    }, [pujadetails, pujaID, setpujahomadetails]);

    // ---- Abandoned-checkout recovery ------------------------------------
    // If an order has been created but the tab closes before payment
    // completes, ping the server (via sendBeacon, which survives unload)
    // so it can follow up on that order.
    const orderIdRef = useRef(null);

    const triggerMailOnClose = useCallback(() => {
        if (orderIdRef.current) {
            const url = `${config.API_BASE_URL}apiCall.php?apicall=triggerMailOnClose&OrderID=${orderIdRef.current}`;
            if (navigator.sendBeacon) {
                navigator.sendBeacon(url);
            }
        }
    }, []);

    useEffect(() => {
        window.addEventListener("beforeunload", triggerMailOnClose);
        return () => {
            window.removeEventListener("beforeunload", triggerMailOnClose);
        };
    }, [triggerMailOnClose]);

    // ---- Step navigation --------------------------------------------------
    const handleNext = () => setActiveStep((prev) => prev + 1);
    const handleBack = () => setActiveStep((prev) => prev - 1);
    const handleEdit = () => setActiveStep((prev) => prev - 1);

    // ---- Payment ------------------------------------------------------
    // Forces a brand-new Razorpay checkout.js script before every payment
    // attempt, so a network drop during one payment can't leave a stale
    // "offline" state in the SDK for the next payment attempt.
    const reloadRazorpayScript = () => {
        return new Promise((resolve) => {
            const existing = document.querySelector(
                'script[src="https://checkout.razorpay.com/v1/checkout.js"]'
            );
            if (existing) existing.remove();

            const script = document.createElement('script');
            script.src = "https://checkout.razorpay.com/v1/checkout.js";
            script.onload = resolve;
            document.body.appendChild(script);
        });
    };

    const handlePayment = useCallback((details) => {
        setloader(true);

        axios.post(config.API_BASE_URL + 'apiCall.php?apicall=pujaOrderCreation', { data: details })
            .then(async (response) => {
                const data = response.data.Data;

                // Remember the order for the abandoned-checkout beacon.
                orderIdRef.current = data.OrderID;
                setgetpaymentdetails((draft) => {
                    draft.OrderID = data.OrderID;
                });

                // Get a fresh Razorpay script before opening checkout.
                await reloadRazorpayScript();

                const options = {
                    key: data.key_id,
                    amount: data.Amount,
                    currency: data.Currency,
                    name: "NSR Trust",
                    description: `Booking for puja ${getpujahomadetails.pujaName}`,
                    image: "https://sridevitemple.com/images/god-image.jpg",
                    order_id: data.OrderID,
                    handler: (res) => {
                        paymentprocess(res);
                        orderIdRef.current = null; // payment succeeded, no need to recover it
                        setloader(false);
                        navigate("/puja-success", { state: { userEmail: details.userEmail } });
                    },
                    prefill: {
                        name: details.pujaUserName,
                        contact: details.pujaUserContact,
                    },
                    notes: {
                        address: "SSRA, Nanganallur",
                    },
                    theme: {
                        color: "#3399cc",
                    },
                    modal: {
                        ondismiss: () => setloader(false)
                    }
                };

                const rzpay = new window.Razorpay(options);

                rzpay.on("payment.failed", (response) => {
                    setloader(false);
                    if (response?.error?.description) {
                        alert(response.error.description);
                    }
                });

                rzpay.open();
                setloader(false);
            })
            .catch((error) => {
                console.log(error);
                setloader(false);
            });
    }, [Razorpay, paymentprocess, navigate, getpujahomadetails.pujaName, setgetpaymentdetails, setloader]);

    // ---- Steps ------------------------------------------------------------
    const steps = [
        {
            label: 'Book A Puja/Homa',
            content: <PujaHomaBookingComponent getpujahomadetails={getpujahomadetails} setpujahomadetails={setpujahomadetails} />,
            nextStep: () => {
                const amount = parseInt(getpujahomadetails.Amount, 10);
                if (
                    amount >= pujadetails.Amount &&
                    getpujahomadetails.pujaDate &&
                    getpujahomadetails.pujaPerson.length > 0 &&
                    getpujahomadetails.pujaDetail.length <= 300
                ) {
                    setpujabookingdetails(getpujahomadetails);
                    handleNext();
                }
            },
            buttons: ['Next']
        },
        {
            label: 'Devotee Details',
            content: <DevoteeDetailsComponent getpujahomauserdetails={getpujahomauserdetails} setpujahomauserdetails={setpujahomauserdetails} />,
            // Mirrors the validation actually performed inside
            // DevoteeDetailsComponent (mandatory: name, 10-digit contact,
            // valid email; optional-but-validated-if-present: alt contact,
            // pincode, PAN, comments).
            nextStep: () => {
                const {
                    pujaUserName,
                    pujaUserContact,
                    pujaUserAlt,
                    pujamailPincode,
                    userEmail,
                    isEmailValid,
                    userPanNo,
                    isPanValid,
                    userComments
                } = getpujahomauserdetails;

                const name = pujaUserName?.trim();
                const contact = pujaUserContact || "";
                const alt = pujaUserAlt || "";
                const pincode = (pujamailPincode || "").toUpperCase().replace(/\s+/g, '');
                const pincodeRegex = /^[A-Z0-9]*$/;

                const isPincodeValid =
                    pincode.length === 0 ||
                    (pincode.length >= 4 && pincode.length <= 10 && pincodeRegex.test(pincode));

                // PAN is optional: valid if left empty, otherwise must pass the format check
                const isPanFieldValid = userPanNo === "" || isPanValid;

                if (userComments && userComments.length > 120) {
                    return;
                }

                const isValid =
                    name &&
                    contact.length === 10 &&
                    (alt.length === 0 || (alt.length === 10 && alt !== contact)) &&
                    isPincodeValid &&
                    userEmail && isEmailValid &&
                    isPanFieldValid;

                if (isValid) {
                    setpujabookingdetails(getpujahomauserdetails);
                    handleNext();
                }
            },
            buttons: ['Back', 'Submit']
        },
        {
            label: 'Confirmation',
            content: <PujaConfirmationComponent />,
            nextStep: () => {
                setloader(true);
                handlePayment(pujabookingdetails);
            },
            buttons: ['Back', 'PayNow']
        },
    ];

    const renderButtons = (step) => {
        return step.buttons.map((button, index) => {
            switch (button) {
                case 'Back':
                    return (
                        <Button
                            sx={{ mt: 1, mr: 1, color: 'grey', borderColor: 'grey !important' }}
                            variant="outlined"
                            key={index}
                            onClick={handleBack}
                        >
                            Back
                        </Button>
                    );
                case 'Next':
                    return (
                        <Button sx={{ mt: 1, mr: 1 }} variant="contained" key={index} onClick={step.nextStep}>
                            Next
                        </Button>
                    );
                case 'Submit':
                    return (
                        <Button sx={{ mt: 1, mr: 1 }} variant="contained" key={index} onClick={step.nextStep}>
                            Submit
                        </Button>
                    );
                case 'Edit':
                    return (
                        <Button
                            sx={{ mt: 1, mr: 1, color: 'grey', borderColor: 'grey !important' }}
                            variant="outlined"
                            key={index}
                            onClick={handleEdit}
                        >
                            Edit
                        </Button>
                    );
                case 'PayNow':
                    return (
                        <Button sx={{ mt: 1, mr: 1 }} variant="contained" key={index} onClick={step.nextStep}>
                            Pay Now
                        </Button>
                    );
                default:
                    return null;
            }
        });
    };

    if (isApiLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <>
            <HeaderComponent />
            <Box sx={{ mt: 3 }}>
                <Container>
                    <Grid container spacing={1} sx={{ mb: 2 }}>
                        <Grid item xs={12} md={5}>
                            <Box className="user-selected-puja" sx={{ backgroundColor: '#ffffff', textAlign: 'center', py: 1, px: 1, borderRadius: '4px' }}>
                                <img
                                    className='puja-image'
                                    src={config.image + pujadetails.Image}
                                    alt={pujadetails.PujaName}
                                    style={{ maxWidth: '100%' }}
                                />
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={7}>
                            <Box>
                                <Typography sx={{ fontSize: '20px', fontWeight: '600', color: '#3875BF', mb: 2 }}>
                                    {pujadetails.PujaName}
                                </Typography>
                                <Typography sx={{ whiteSpace: 'pre-line', lineHeight: 2.3, color: '#8E8E8E' }}>
                                    {pujadetails.Description}
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                    <Divider />
                </Container>
            </Box>

            <Box className="mypuja-booking-screen">
                <Container>
                    <Box sx={{ py: 2 }}>
                        <Stepper activeStep={activeStep} orientation="vertical">
                            {steps.map((step) => (
                                <Step key={step.label}>
                                    <Box className="stepper-label">
                                        <StepLabel>
                                            <Typography variant="h6">{step.label}</Typography>
                                        </StepLabel>
                                    </Box>
                                    <StepContent>
                                        {step.content}
                                        <Box className="stepContent-btn" sx={{ mb: 2, mt: 2, textAlign: 'right' }}>
                                            {getloader ? (
                                                <CircularProgress size={24} />
                                            ) : (
                                                renderButtons(step)
                                            )}
                                        </Box>
                                        <Divider />
                                    </StepContent>
                                </Step>
                            ))}
                        </Stepper>
                    </Box>
                </Container>
                <FooterComponent />
            </Box>
            <BottomAppBarComponent />
        </>
    );
};

const mapStateToProps = (state) => ({
    pujadetails: state.puja.pujadetails,
    pujaorderdetails: state.puja.pujaorderdetails,
    pujabookingdetails: state.puja.pujabookingdetails
});

export default connect(mapStateToProps, {
    paymentprocess,
    getpujadetail,
    setpujabookingdetails,
    setpaymentdetails,
    bookPujaComplete
})(MyPujaBookingComponent);